/**
 * Shared helpers for detecting whether the learner is embedded inside an
 * external host (LMS / SCORM player / RXD wrapper).
 *
 * Mirrors the historical referrer-based gating that was duplicated across
 * ~10 components: skip parent-frame integration when the embedding page is
 * one of our own first-party origins.
 */

const FIRST_PARTY_DOMAINS = new Set<string>([
  'localhost:8000',
  'www.easyllama.com'
]);

/**
 * Returns `true` when we are allowed to talk to the parent frame for LMS
 * integration purposes. This intentionally treats "no referrer" as
 * permitted (matches the prior behavior in components like `Success.tsx`).
 */
export function shouldUseParentHost(): boolean {
  try {
    const referrer = window?.document?.referrer;
    if (!referrer) {
      return true;
    }
    const domain = referrer.split('/')[2];
    return !FIRST_PARTY_DOMAINS.has(domain);
  } catch {
    return false;
  }
}

/**
 * Safely access `window.parent`. Returns `undefined` if the parent is
 * cross-origin and access throws, or if `window` is unavailable
 * (e.g. SSR / vitest jsdom edge cases).
 */
export function getParentWindow(): Window | undefined {
  try {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return window.parent ?? undefined;
  } catch {
    return undefined;
  }
}
